/**
 * Calendar triage: which Auger event schema a Google Calendar event becomes,
 * its Pipedrive activity type, the record it's about, and whether it still
 * needs triaging. Triage state lives in the event's private extendedProperties
 * (`eventCategory`, `activityId`, `augerEventId`, `augerNoteId`, `note`).
 *
 * Schemas in the `management` category are Auger-only: triaging one creates an
 * Auger event record but no Pipedrive activity, and it carries no resource.
 * Register a new event schema here so the triage UI and the MCP both offer it.
 */

/** A Google Calendar event, as far as triage reads it. */
export interface CalendarEvent {
  attendees?: Array<{ email?: string; self?: boolean; responseStatus?: string }> | null
  extendedProperties?: { private?: Record<string, string> | null } | null
}

/** Every triage category, in picker order. */
export const CATEGORIES = ["portfolio", "pipeline", "networking", "management", "personal", "other"]

export interface CategorySchema {
  schema: string
  /** The Pipedrive activity type; null for an Auger-only schema. */
  pipedriveType: string | null
}

export const CATEGORY_SCHEMAS: Record<string, CategorySchema[]> = {
  portfolio: [
    { schema: "Partner Office Hours", pipedriveType: "partner_meeting" },
    { schema: "Board Meeting", pipedriveType: "board_meeting" },
    { schema: "Staff Office Hours", pipedriveType: "office_hours" },
    // Pipedrive has no onboarding activity type; existing onboarding
    // activities are all logged as partner_meeting.
    { schema: "Portfolio Onboarding", pipedriveType: "partner_meeting" },
  ],
  pipeline: [{ schema: "Pipeline Meeting", pipedriveType: "pipeline_meeting" }],
  networking: [
    { schema: "Networking Meeting", pipedriveType: "meeting" },
    { schema: "Introduction", pipedriveType: "introduction" },
    { schema: "Program Event", pipedriveType: "meeting" },
    { schema: "Social/Dinner", pipedriveType: "founder_social" },
  ],
  management: [{ schema: "Internal Meeting", pipedriveType: null }],
  // Not a triage category: imported conferences, travel and holidays, which the
  // Events page colours and filters by but no calendar event is triaged into.
  community: [
    { schema: "Community Event", pipedriveType: null },
    { schema: "Travel", pipedriveType: null },
    { schema: "Holiday", pipedriveType: null },
  ],
}

/** The record type a category's events are about. `management` events are
 *  internal meetings with no counterparty, so they have none. */
export const CATEGORY_RESOURCE_TYPES: Record<string, string> = {
  pipeline: "deal",
  portfolio: "organization",
  networking: "person",
}

/** The categories whose events are about a resource and become activities. */
const OUTWARD_CATEGORIES = Object.keys(CATEGORY_RESOURCE_TYPES)

export const schemasForCategory = (category: string | null | undefined): CategorySchema[] =>
  (category && CATEGORY_SCHEMAS[category]) || []

export const resourceTypeFor = (category: string | null | undefined): string | null =>
  (category && CATEGORY_RESOURCE_TYPES[category]) || null

/** The Pipedrive activity type for a schema name; null when the schema is
 *  Auger-only or unregistered. */
export function pipedriveTypeFor(schemaName: string | null | undefined): string | null {
  for (const list of Object.values(CATEGORY_SCHEMAS)) {
    const match = list.find((s) => s.schema === schemaName)
    if (match) return match.pipedriveType
  }
  return null
}

/** The category an event schema is registered under; null when the schema is
 *  in none (an LP Meeting, say). */
export function categoryForSchema(schemaName: string | null | undefined): string | null {
  for (const [category, list] of Object.entries(CATEGORY_SCHEMAS)) {
    if (list.some((s) => s.schema === schemaName)) return category
  }
  return null
}

/** The link field an event's resource occupies: its record's type, for the
 *  types that are resources. Every event schema declares these keys, never a
 *  generic `resource` one. */
export const resourceLinkKey = (recordType: string): string | null =>
  Object.values(CATEGORY_RESOURCE_TYPES).includes(recordType) ? recordType : null

const privateProps = (event: CalendarEvent) => event.extendedProperties?.private || {}

export const eventCategory = (event: CalendarEvent | null | undefined): string | null =>
  event?.extendedProperties?.private?.eventCategory ?? null

/** The user declined the event. Matched by `email` when given; otherwise by
 *  Google's `self` flag, which marks the calendar owner's own attendee. */
export function isDeclined(event: CalendarEvent, email?: string | null): boolean {
  const attendees = event.attendees || []
  const me = attendees.find((a) => (email ? a.email === email : a.self))
  return me?.responseStatus === "declined"
}

/** The event still needs triaging. */
export function needsTriage(event: CalendarEvent, requireNotes = false): boolean {
  const category = eventCategory(event)
  if (!category) return true

  const { activityId, note, augerEventId, augerNoteId } = privateProps(event)
  if (OUTWARD_CATEGORIES.includes(category)) {
    if (!activityId && !augerEventId) return true
    const hasNotes = note === "true" || !!augerNoteId
    return requireNotes && !hasNotes
  }

  // An internal meeting is triaged once it has an Auger event; notes are never
  // required. A solo event (no attendees) needs nothing at all.
  if (category === "management") return !!event.attendees?.length && !augerEventId

  return false
}
